import { useEffect, useRef } from 'react';

/**
 * Background partículas + ímã (mouse/touch) conforme GUIA-EFEITO-BG-MOUSE.
 * Detecta modo claro com `document.documentElement.classList.contains('dark')` invertido.
 */
export function AnimatedBackground() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const mouse = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
    const smooth = { x: mouse.x, y: mouse.y };

    const onMove = (e) => {
      mouse.x = e.clientX;
      mouse.y = e.clientY;
    };
    const onTouch = (e) => {
      if (e.touches[0]) {
        mouse.x = e.touches[0].clientX;
        mouse.y = e.touches[0].clientY;
      }
    };

    if (!reducedMotion) {
      window.addEventListener('mousemove', onMove, { passive: true });
      window.addEventListener('touchmove', onTouch, { passive: true });
    }

    let animationId = 0;
    let particles = [];

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      mouse.x = Math.min(mouse.x, canvas.width);
      mouse.y = Math.min(mouse.y, canvas.height);
      smooth.x = Math.min(smooth.x, canvas.width);
      smooth.y = Math.min(smooth.y, canvas.height);
      for (const p of particles) {
        p.bx = Math.max(0, Math.min(canvas.width, p.bx));
        p.by = Math.max(0, Math.min(canvas.height, p.by));
        p.x = Math.max(0, Math.min(canvas.width, p.x));
        p.y = Math.max(0, Math.min(canvas.height, p.y));
      }
    };

    resize();
    window.addEventListener('resize', resize);

    const particleCount = 72;
    for (let i = 0; i < particleCount; i++) {
      const bx = Math.random() * canvas.width;
      const by = Math.random() * canvas.height;
      particles.push({
        bx,
        by,
        x: bx,
        y: by,
        vx: (Math.random() - 0.5) * 0.38,
        vy: (Math.random() - 0.5) * 0.38,
        size: Math.random() * 2.4 + 0.85,
        opacity: Math.random() * 0.38 + 0.22,
        hue: Math.random() > 0.42 ? 262 : 155,
      });
    }

    let time = 0;
    const mouseLerp = 0.085;
    const filmSpring = 0.145;
    const magnetBlendMax = 0.44;

    const animate = () => {
      time += 0.002;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      if (!reducedMotion) {
        smooth.x += (mouse.x - smooth.x) * mouseLerp;
        smooth.y += (mouse.y - smooth.y) * mouseLerp;
      }

      const magnetR = Math.min(320, canvas.width * 0.26, canvas.height * 0.32);
      const innerSoft = 72;

      const isDark = document.documentElement.classList.contains('dark');

      for (const p of particles) {
        p.bx += p.vx;
        p.by += p.vy;
        if (p.bx < 0 || p.bx > canvas.width) p.vx *= -1;
        if (p.by < 0 || p.by > canvas.height) p.vy *= -1;

        let tx = p.bx;
        let ty = p.by;

        if (!reducedMotion) {
          const mdx = smooth.x - p.bx;
          const mdy = smooth.y - p.by;
          const md = Math.hypot(mdx, mdy);
          if (md > 0.5 && md < magnetR) {
            let w = (1 - md / magnetR) ** 1.65 * magnetBlendMax;
            if (md < innerSoft) w *= md / innerSoft;
            tx = p.bx + mdx * w;
            ty = p.by + mdy * w;
          }
        }

        p.x += (tx - p.x) * filmSpring;
        p.y += (ty - p.y) * filmSpring;
      }

      const isLight = !isDark;
      const lineAlphaBase = isLight ? 0.18 : 0.082;
      const linkReach = 168;
      const particleLight = isLight ? 42 : 58;

      ctx.lineCap = 'round';
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const a = particles[i];
          const b = particles[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < linkReach) {
            const fade = 1 - dist / linkReach;
            const hasGreen = a.hue === 155 || b.hue === 155;
            const hue = a.hue === b.hue ? a.hue : hasGreen ? 162 : 262;
            const lineAlphaBoost = hasGreen ? 1.22 : 1;
            ctx.beginPath();
            ctx.strokeStyle = `hsla(${hue}, 92%, ${
              isLight ? (hasGreen ? '40%' : '48%') : hasGreen ? '58%' : '60%'
            }, ${lineAlphaBase * lineAlphaBoost * fade * fade})`;
            ctx.lineWidth = 0.65 + fade * 0.55;
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }

      for (const p of particles) {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        const pulse = Math.sin(time * 2 + p.bx * 0.01) * 0.12;
        const isGreen = p.hue === 155;
        const nodeLight = isGreen ? (isLight ? particleLight - 4 : particleLight + 6) : particleLight;
        const nodeAlpha = isGreen ? Math.min(0.98, p.opacity + pulse + 0.09) : Math.min(0.95, p.opacity + pulse);
        ctx.fillStyle = `hsla(${p.hue}, ${isGreen ? '94%' : '92%'}, ${nodeLight}%, ${nodeAlpha})`;
        ctx.fill();
      }

      animationId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', resize);
      if (!reducedMotion) {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('touchmove', onTouch);
      }
    };
  }, []);

  return (
    <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden" aria-hidden>
      <div
        className="absolute -left-1/4 -top-1/4 h-[640px] w-[640px] rounded-full opacity-[34%] dark:opacity-[32%]"
        style={{
          background:
            'radial-gradient(circle at 40% 35%, hsl(262 90% 58% / 0.72) 0%, hsl(262 85% 52% / 0.28) 38%, transparent 62%)',
          animation: 'blob-move 25s ease-in-out infinite',
          filter: 'saturate(1.15)',
        }}
      />
      <div
        className="absolute -bottom-1/4 -right-1/4 h-[560px] w-[560px] rounded-full opacity-[26%] dark:opacity-[42%]"
        style={{
          background:
            'radial-gradient(circle at 60% 55%, hsl(155 95% 50% / 0.7) 0%, hsl(165 88% 44% / 0.3) 40%, transparent 64%)',
          animation: 'blob-move 30s ease-in-out infinite reverse',
          filter: 'saturate(1.24)',
        }}
      />
      <div
        className="absolute left-1/2 top-1/2 h-[720px] w-[720px] -translate-x-1/2 -translate-y-1/2 rounded-full opacity-20 dark:opacity-[22%]"
        style={{
          background:
            'radial-gradient(circle at 50% 50%, hsl(168 72% 48% / 0.52) 0%, hsl(200 52% 32% / 0.08) 48%, transparent 68%)',
          animation: 'blob-move 20s ease-in-out infinite 5s',
          filter: 'saturate(1.1)',
        }}
      />

      <div
        className="absolute inset-0 opacity-[0.09] dark:hidden"
        style={{
          backgroundImage: `linear-gradient(hsl(155 82% 40% / 0.5) 1px, transparent 1px),
                            linear-gradient(90deg, hsl(165 80% 38% / 0.44) 1px, transparent 1px)`,
          backgroundSize: '40px 40px',
          animation: 'grid-scroll 8s linear infinite',
        }}
      />
      <div
        className="absolute inset-0 hidden opacity-[0.044] dark:block"
        style={{
          backgroundImage: `linear-gradient(hsl(158 42% 48% / 0.28) 1px, transparent 1px),
                            linear-gradient(90deg, hsl(168 36% 42% / 0.22) 1px, transparent 1px)`,
          backgroundSize: '40px 40px',
          animation: 'grid-scroll 8s linear infinite',
        }}
      />
      <div className="grain-dark pointer-events-none absolute inset-0 hidden opacity-40 mix-blend-overlay dark:block" />

      <canvas ref={canvasRef} className="absolute inset-0 h-full w-full" />
    </div>
  );
}
